import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { officialsApi } from '../../services/api';
import { usePaginatedApi } from '../../hooks/useApi';
import { LoadingState } from '../loading/LoadingState';
import { ErrorBoundary } from '../errors/ErrorBoundary';

interface OfficialCard {
  id: string;
  name: string;
  title: string;
  position: string;
  state: string;
  party: string;
  shittyVotes: number;
  totalVotes: number;
}

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'DC', 'FL',
  'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME',
  'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH',
  'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI',
  'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
];

interface OfficialListProps {
  state?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  pageSize?: number;
}

const OfficialsList: React.FC<OfficialListProps> = ({
  state,
  sortBy = 'shittyVotes',
  sortOrder = 'desc',
  pageSize = 20,
}) => {
  const navigate = useNavigate();
  const [currentState, setCurrentState] = useState(state);
  const [currentSort, setCurrentSort] = useState(sortBy);
  const [currentOrder, setCurrentOrder] = useState<'asc' | 'desc'>(sortOrder);

  const {
    data: officials,
    isLoading,
    isLoadingMore,
    error,
    hasMore,
    execute: refreshList,
    loadMore,
  } = usePaginatedApi(
    (params, cancelToken) =>
      officialsApi.list(
        {
          state: currentState,
          sortBy: currentSort,
          sortOrder: currentOrder,
          ...params,
        },
        { cancelToken }
      ),
    {
      pageSize,
      onError: (error) => {
        console.error('Error loading officials:', error);
      },
    }
  );

  const handleStateChange = useCallback((newState: string) => {
    setCurrentState(newState);
    // Pass the new value explicitly because component state
    // updates are async and the closure would still hold the old one.
    refreshList({ state: newState });
  }, [refreshList]);

  const handleSortChange = useCallback((field: string) => {
    const nextOrder: 'asc' | 'desc' =
      field === currentSort && currentOrder === 'desc' ? 'asc' : 'desc';
    setCurrentSort(field);
    setCurrentOrder(nextOrder);
    refreshList({ sortBy: field, sortOrder: nextOrder });
  }, [currentSort, currentOrder, refreshList]);

  const handleOfficialClick = useCallback((id: string) => {
    navigate(`/officials/${id}`);
  }, [navigate]);

  const renderOfficialCard = useCallback((official: OfficialCard) => {
    const shittyPercent = official.totalVotes > 0
      ? (official.shittyVotes / official.totalVotes) * 100
      : 0;

    return (
      <div
        key={official.id}
        className="official-card card mb-3 cursor-pointer hover-shadow"
        onClick={() => handleOfficialClick(official.id)}
      >
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <h5 className="card-title mb-1">{official.name}</h5>
              <p className="card-subtitle text-muted mb-2">
                {official.title} - {official.state} ({official.party})
              </p>
            </div>
            <div className="text-end">
              <div className="h4 mb-0 text-danger">
                {shittyPercent.toFixed(1)}%
              </div>
              <small className="text-muted">
                {official.shittyVotes} / {official.totalVotes} votes
              </small>
            </div>
          </div>
        </div>
      </div>
    );
  }, [handleOfficialClick]);

  const renderSortButton = (field: string, label: string) => (
    <button
      className={`btn btn-sm ${currentSort === field ? 'btn-primary' : 'btn-outline-primary'}`}
      onClick={() => handleSortChange(field)}
    >
      {label}
      {currentSort === field && (
        <i className={`ms-1 bi bi-arrow-${currentOrder === 'asc' ? 'up' : 'down'}`} />
      )}
    </button>
  );

  return (
    <ErrorBoundary>
      <div className="officials-list">
        <div className="controls mb-4">
          <div className="row g-3">
            <div className="col-md-6">
              <select
                className="form-select"
                value={currentState || ''}
                onChange={(e) => handleStateChange(e.target.value)}
              >
                <option value="">All States</option>
                {US_STATES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-6">
              <div className="btn-group">
                {renderSortButton('shittyVotes', 'Shitty Votes')}
                {renderSortButton('totalVotes', 'Total Votes')}
                {renderSortButton('name', 'Name')}
                {renderSortButton('state', 'State')}
              </div>
            </div>
          </div>
        </div>

        <LoadingState
          isLoading={isLoading}
          error={error}
          retry={refreshList}
          loadingMessage="Loading officials..."
          errorMessage="Error loading officials"
        >
          <div className="officials-grid">
            {officials?.map(renderOfficialCard)}
            
            {isLoadingMore && (
              <div className="text-center my-3">
                <div className="spinner-border text-primary">
                  <span className="visually-hidden">Loading more...</span>
                </div>
              </div>
            )}

            {hasMore && !isLoadingMore && !error && (
              <div className="text-center my-3">
                <button
                  className="btn btn-outline-primary"
                  onClick={() => loadMore()}
                >
                  Load More
                </button>
              </div>
            )}

            {!hasMore && officials?.length === 0 && (
              <div className="text-center text-muted my-5">
                <p>No officials found</p>
              </div>
            )}
          </div>
        </LoadingState>
      </div>
    </ErrorBoundary>
  );
};

export default OfficialsList;